import { useState } from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FileDown, Loader2, FolderOpen, Send, FolderArchive } from "lucide-react";
import { exportDoc } from "../../pages/ExamSessionPage";

const GROUPS = [
  {
    title: "Avant la session",
    docs: [
      { key: "sommaire", label: "Sommaire général du dossier", desc: "Liste des documents et chronologie J-15 → J+15" },
      { key: "convocation-candidats", label: "Convocations candidats", desc: "Une convocation par candidat inscrit", needs: "candidats", mail: "candidats" },
      { key: "convocation-jury", label: "Convocations membres du jury", desc: "Une convocation par membre du jury", needs: "jury", mail: "jury" },
      { key: "inscription", label: "Dossier d'inscription candidat", desc: "Formulaire vierge : identité, prérequis, pièces justificatives" },
      { key: "reglement", label: "Règlement intérieur de l'examen", desc: "Remis à chaque candidat avant les épreuves" },
      { key: "checklist", label: "Checklist d'organisation", desc: "J-15 → J+15 avec l'état d'avancement actuel" },
    ],
  },
  {
    title: "Jour J",
    docs: [
      { key: "emargement", label: "Feuille d'émargement", desc: "Signatures des candidats par épreuve" },
      { key: "subjects", label: "Sujets de mise en situation", desc: "Dossier confidentiel — enveloppes scellées", needs: "sujets" },
      { key: "qcm", label: "QCM — Épreuve 1", desc: "Un exemplaire par candidat", needs: "qcm" },
      { key: "qcm-corrige", label: "Corrigé QCM (jury)", desc: "Document confidentiel jury", needs: "qcm" },
      { key: "grilles-jury", label: "Grilles d'évaluation jury", desc: "Une grille par candidat, compétence par compétence", needs: "candidats" },
      { key: "presentation-jury", label: "Fiche de présentation du jury", desc: "Composition + déclaration de conformité", needs: "jury" },
      { key: "guide-jury", label: "Guide du jury", desc: "Niveaux d'évaluation et posture d'évaluateur" },
    ],
  },
  {
    title: "Après la session",
    docs: [
      { key: "pv", label: "PV de délibération", desc: "Résultats individuels et signatures du jury" },
      { key: "attestations", label: "Attestations de présence", desc: "Une attestation par candidat présent", needs: "candidats" },
      { key: "suivi-rattrapages", label: "Suivi ajournements & rattrapages", desc: "Candidats ajournés et échéances (12 mois)" },
    ],
  },
];

const MISSING = {
  candidats: "Ajoutez d'abord des candidats",
  jury: "Ajoutez d'abord les membres du jury",
  sujets: "Générez d'abord les sujets",
  qcm: "Générez d'abord le QCM",
};

export default function DocumentsTab({ api, session, onChange }) {
  const [busy, setBusy] = useState(null);
  const [sending, setSending] = useState(null);
  const [zipping, setZipping] = useState(false);

  const availability = {
    candidats: (session.candidates || []).length > 0,
    jury: (session.jury || []).length > 0,
    sujets: (session.sujets || []).length > 0,
    qcm: !!(session.qcm_genere?.questions || []).length,
  };

  const download = async (key) => {
    setBusy(key);
    await exportDoc(api, session.session_id, `${key}.docx`);
    setBusy(null);
  };

  const downloadZip = async () => {
    setZipping(true);
    await exportDoc(api, session.session_id, "dossier.zip");
    setZipping(false);
  };

  const lastSend = (target) =>
    [...(session.convocations_log || [])].reverse().find(l => l.target === target);

  const sendConvocations = async (target) => {
    const label = target === "candidats" ? "aux candidats" : "aux membres du jury";
    if (!window.confirm(`Envoyer les convocations par email ${label} (avec le document joint) ?`)) return;
    setSending(target);
    try {
      const { data } = await api.post(`/certif/sessions/${session.session_id}/send-convocations`, { target });
      const sansEmail = data.results.filter(r => r.status === "sans_email").length;
      const failed = data.results.filter(r => ["failed", "skipped"].includes(r.status));
      if (data.sent > 0) {
        toast.success(`${data.sent} convocation(s) envoyée(s)${sansEmail ? ` — ${sansEmail} destinataire(s) sans email` : ""}`);
      } else {
        toast.error(failed[0]?.reason || `Aucun envoi possible${sansEmail ? ` (${sansEmail} destinataire(s) sans email)` : ""}`);
      }
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(null); }
  };

  return (
    <div className="space-y-5" data-testid="documents-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-1 min-w-[280px]">
          <FolderOpen className="w-3.5 h-3.5 text-[#f7c935]" />
          Dossier complet d'examen — chaque document est généré en DOCX, pré-rempli avec les données de la session.
        </p>
        <Button onClick={downloadZip} disabled={zipping}
          className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="download-dossier-zip-btn">
          {zipping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderArchive className="w-4 h-4 mr-1.5" />}
          Dossier complet (ZIP)
        </Button>
      </div>
      {GROUPS.map(g => (
        <div key={g.title}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#f7c935] mb-2">{g.title}</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2">
            {g.docs.map(d => {
              const blocked = d.needs && !availability[d.needs];
              const log = d.mail ? lastSend(d.mail) : null;
              return (
                <div key={d.key} className={`bg-[#14101c] border rounded-xl p-3.5 flex items-start gap-3 ${blocked ? "border-white/5 opacity-60" : "border-white/5 hover:border-white/15"} transition-colors`}
                  data-testid={`doc-card-${d.key}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white">{d.label}</p>
                    <p className="text-[10px] text-slate-500">{blocked ? MISSING[d.needs] : d.desc}</p>
                    {log && (
                      <p className="text-[10px] text-emerald-400 mt-1" data-testid={`convocations-last-send-${d.mail}`}>
                        ✉ Dernier envoi : {(log.at || "").slice(0, 16).replace("T", " ")} — {log.sent}/{log.total} envoyé(s)
                      </p>
                    )}
                    {d.key === "convocation-candidats" && lastSend("rappel-j3") && (
                      <p className="text-[10px] text-amber-400 mt-0.5" data-testid="rappel-j3-last-send">
                        ⏰ Rappel J-3 : {(lastSend("rappel-j3").at || "").slice(0, 16).replace("T", " ")} — {lastSend("rappel-j3").sent}/{lastSend("rappel-j3").total} envoyé(s)
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <Button size="sm" variant="outline" disabled={blocked || busy === d.key}
                      onClick={() => download(d.key)}
                      className="border-white/10 text-slate-300 hover:bg-white/5 h-8 text-xs"
                      title="Télécharger le DOCX"
                      data-testid={`doc-download-${d.key}`}>
                      {busy === d.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                    </Button>
                    {d.mail && (
                      <Button size="sm" variant="outline" disabled={blocked || sending === d.mail}
                        onClick={() => sendConvocations(d.mail)}
                        className="border-[#f7c935]/40 text-[#f7c935] hover:bg-[#f7c935]/10 h-8 text-xs"
                        title="Envoyer par email avec le document joint"
                        data-testid={`send-convocations-${d.mail}-btn`}>
                        {sending === d.mail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
